import {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  exponentialDelay,
  getFetcher,
  isNetworkError,
} from '@glovo/fetcher'
import { getConfig } from '@/tools/config'
import { glovoRum } from '@/tools/o11y/client/'

type RetryCallback = (
  retryCount: number,
  error: AxiosError,
  requestConfig: AxiosRequestConfig<unknown>
) => void

export type SetRequestHeaders = (key: string, value: string | undefined | null | number) => void

interface FetcherOptions {
  targetEnv?: 'client' | 'server'
}

export const createFetcher = (options: FetcherOptions = { targetEnv: 'server' }) => {
  let fetcherInstance: AxiosInstance

  const authRetryHandler: { __cb: RetryCallback | null } = { __cb: null }
  const headers: Record<string, string> = {}

  const retryCondition = (error: AxiosError) => {
    if (isNetworkError(error)) {
      return true
    }

    const status = error.response?.status

    if (typeof status !== 'number') {
      return false
    }
    return authRetryHandler.__cb && status
      ? status > 400 && status <= 599
      : status > 500 && status <= 599
  }

  const initFetcher = () => {
    const appConfig = getConfig()
    fetcherInstance = getFetcher({
      platform: appConfig.platform,
      baseURL: appConfig.apiUrl,
      requestTTL: appConfig.requestTTL,
      appConfig: {
        name: appConfig.name,
        project: appConfig.project,
        version: appConfig.version,
        type: appConfig.appType,
        developmentState: appConfig.developmentState,
      },
      headers: {
        'Glovo-Api-Version': appConfig.apiVersion,
      },
      retriesConfig: {
        retries: 3,
        retryCondition,
        retryDelay: exponentialDelay,
        onRetry: (retryCount, error, requestConfig) => {
          if (authRetryHandler.__cb) {
            authRetryHandler.__cb(retryCount, error, requestConfig)
          }
        },
      },
    })

    if (options.targetEnv === 'server' && getConfig().isBrowser) {
      fetcherInstance.interceptors.request.use((config) => {
        // eslint-disable-next-line @glovo/custom-rules/no-console
        console.error('❌ CLient fetcher should not be used on the server side')
        return config
      })
    }

    fetcherInstance.interceptors.request.use(
      (config) => {
        for (const key in headers) {
          const value = headers[key]
          if (value) {
            config.headers[key] = value
          } else {
            delete config.headers[key]
          }
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    //TODO remove this interceptor when the last-browsed-location o11y is not needed anymore
    fetcherInstance.interceptors.request.use(
      (config) => {
        if (typeof window === 'undefined') {
          return config
        }
        glovoRum.addAction('network_request.location_headers', {
          'Glovo-Location-City-Code': config.headers['Glovo-Location-City-Code'],
          'Glovo-Location-Country-Code': config.headers['Glovo-Location-Country-Code'],
          'Glovo-Delivery-Location-Longitude': config.headers['Glovo-Delivery-Location-Longitude'],
          'Glovo-Delivery-Location-Latitude': config.headers['Glovo-Delivery-Location-Latitude'],
        })

        return config
      },
      (error) => Promise.reject(error)
    )

    return fetcherInstance
  }

  const setRequestHeaders: SetRequestHeaders = (key, value) => {
    if (!fetcherInstance) {
      initFetcher()
    }

    headers[key] = value ? String(value) : ''
  }

  let refreshRequest: Promise<{ accessToken: string }> | null = null
  const setAuthorization = (
    { accessToken }: { accessToken: string },
    onRefresh: () => Promise<{ accessToken: string }>,
    onError: (e: Error) => Promise<void>
  ) => {
    if (!fetcherInstance) {
      initFetcher()
    }

    fetcherInstance.defaults.headers.common['Authorization'] = accessToken
    authRetryHandler.__cb = async (_retryCount, error, requestConfig) => {
      if (error.response?.status === 401) {
        try {
          if (!refreshRequest) {
            refreshRequest = onRefresh()
          }
          const { accessToken } = await refreshRequest
          fetcherInstance.defaults.headers.common['Authorization'] = accessToken

          if (requestConfig.headers) {
            requestConfig.headers['Authorization'] = accessToken
          }

          refreshRequest = null
          // eslint-disable-next-line @glovo/custom-rules/always-forward-error
        } catch (refreshError) {
          await onError(refreshError as Error)
        }
      }
    }
  }

  const removeAuthorization = () => {
    if (!fetcherInstance) {
      throw new Error('Fetcher is not initialized')
    }
    fetcherInstance.defaults.headers.common['Authorization'] = null
    authRetryHandler.__cb = null
  }

  // this workaround is needed as app config is not available build time
  const fetcher = new Proxy({} as AxiosInstance, {
    get(_target: AxiosInstance, prop: keyof AxiosInstance) {
      if (!fetcherInstance) {
        initFetcher()
      }
      return fetcherInstance[prop]
    },
  })

  return {
    fetcher,
    DO_NOT_USE_OUTSIDE_OF_TESTS: { authRetryHandler, headers },
    setRequestHeaders,
    removeAuthorization,
    setAuthorization,
    retryCondition,
  }
}
